import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import footerLogo from '../assets/autoboy_logo3.png';
import SupportChat from './SupportChat';

const Footer = () => {
  const particlesRef = useRef(null);
  const navigate = useNavigate();
  const [supportChatOpen, setSupportChatOpen] = useState(false);

  // Create floating particles effect
  useEffect(() => {
    const createParticles = () => {
      const container = particlesRef.current;
      if (!container) return;

      // Clear existing particles
      container.innerHTML = '';

      // Create particles
      for (let i = 0; i < 30; i++) {
        const particle = document.createElement('div');
        particle.className = 'footer-particle';

        // Random properties
        const size = Math.random() * 4 + 2;
        const x = Math.random() * 100;
        const delay = Math.random() * 10;
        const duration = Math.random() * 20 + 10;

        particle.style.cssText = `
          width: ${size}px;
          height: ${size}px;
          left: ${x}%;
          bottom: -10px;
          animation-delay: ${delay}s;
          animation-duration: ${duration}s;
          background: rgba(34, 197, 94, ${Math.random() * 0.5 + 0.1});
        `;

        container.appendChild(particle);
      }
    };

    createParticles();
    const interval = setInterval(createParticles, 15000);

    return () => clearInterval(interval);
  }, []);

  return (
    <footer className="footer">
      {/* Background Layers */}
      <div className="footer-bg"></div>
      <div className="footer-gradient"></div>
      
      {/* Floating Particles */}
      <div className="footer-particles" ref={particlesRef}></div>

      {/* Glass Container */}
      <div className="footer-glass">
        {/* Top Navigation */}
        <nav className="footer-nav">
          <div className="footer-nav-content">
            <a href="/homepage" className="footer-logo">
              <div className="footer-logo-text">
                <img src={footerLogo} alt="AutoboyExpress Logo" />
              </div>
            </a>

            <div className="footer-nav-links">
              <a href="/shop" className="footer-nav-link">Shop</a>
              <a href="/swap-deals" className="footer-nav-link">Swap Deals</a>
              <a href="/become-seller" className="footer-nav-link">Become a Seller</a>
            </div>

            <div className="footer-auth">
              <a href="/register" className="footer-btn footer-btn-outline">Join</a>
              <a href="/login" className="footer-btn footer-btn-primary">Login</a>
            </div>
          </div>
        </nav>

        {/* Main Footer Content */}
        <div className="footer-content">
          <div className="footer-grid">
            {/* Shop & Services */}
            <div className="footer-column">
              <h3><i className="fas fa-store"></i> Shop & Services</h3>
              <div className="footer-links">
                <a href="/shop" className="footer-link">
                  <i className="fas fa-shopping-bag"></i>
                  <span>
                    <strong>Browse Products</strong><br />
                    <small>Explore our range of phones and accessories.</small>
                  </span>
                </a>
                <a href="/swap-deals" className="footer-link">
                  <i className="fas fa-exchange-alt"></i>
                  <span>
                    <strong>Swap Gadgets</strong><br />
                    <small>Trade your old devices for new ones.</small>
                  </span>
                </a>
                <a href="/become-seller" className="footer-link">
                  <i className="fas fa-store-alt"></i>
                  <span>
                    <strong>Become a Seller</strong><br />
                    <small>Start selling on our platform today.</small>
                  </span>
                </a>
              </div>
            </div>

            {/* Support & Help */}
            <div className="footer-column">
              <h3><i className="fas fa-headset"></i> Support & Help</h3>
              <div className="contact-info">
                <div className="contact-item">
                  <i className="fas fa-life-ring"></i>
                  <span>
                    <a href="/help-center" className="contact-link"><strong>Help Centre</strong></a>
                    <small>Find answers to common questions and guides.</small>
                  </span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-gavel"></i>
                  <span>
                    <a href="/disputes" className="contact-link"><strong>Disputes</strong></a>
                    <small>Report and manage order disputes.</small>
                  </span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-file-contract"></i>
                  <span>
                    <a href="/terms-and-conditions" className="contact-link"><strong>Terms &amp; Conditions</strong></a>
                    <small>Read our full terms of service.</small>
                  </span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-shield-alt"></i>
                  <span>
                    <a href="/privacy-policy" className="contact-link"><strong>Privacy Policy</strong></a>
                    <small>How we collect and protect your data.</small>
                  </span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-money-bill-wave"></i>
                  <span>
                    <a href="/refund-policy" className="contact-link"><strong>Refund Policy</strong></a>
                    <small>Understand our escrow and refund process.</small>
                  </span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-balance-scale"></i>
                  <span>
                    <a href="/acceptable-use-policy" className="contact-link"><strong>Acceptable Use Policy</strong></a>
                    <small>Rules for using AutoboyExpress.</small>
                  </span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-at"></i>
                  <span>
                    <a href="mailto:support@autoboyexpress.com" className="contact-link"><strong>support@autoboyexpress.com</strong></a>
                    <small>Reach out for assistance or inquiries.</small>
                  </span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>
                    <strong>3/9 olukolesho street tayeolu tower</strong>
                    <small>Visit our headquarters.</small>
                  </span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-phone"></i>
                  <span>
                    <a href="tel:09136941527" className="contact-link"><strong>09136941527</strong></a> / <a href="tel:08110749313" className="contact-link"><strong>08110749313</strong></a>
                    <small>Call our support line.</small>
                  </span>
                </div>
                <motion.button
                  onClick={() => setSupportChatOpen(true)}
                  className="live-chat-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <i className="fas fa-comments"></i>
                  <span>Live Chat Support</span>
                </motion.button>
              </div>
            </div>

            {/* Account & Orders */}
            <div className="footer-column quick">
              <h3><i className="fas fa-user-circle"></i> Account & Orders</h3>
              <div className="footer-links">
                <a href="/login" className="footer-link">Login / Sign Up</a>
                <a href="/orders" className="footer-link">My Orders</a>
                <a href="/wishlist" className="footer-link">Wishlist</a>
                <a href="/cart" className="footer-link">Shopping Cart</a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <div className="footer-copyright">
              © 2025 AutoboyExpress - All rights reserved
            </div>

            <div className="footer-social">
              <motion.a
                href="https://twitter.com/AutoboyExpress"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                data-tooltip="Follow us on X"
                whileHover={{ scale: 1.2, rotate: 10 }}
                whileTap={{ scale: 0.9 }}
              >
                <i className="fab fa-x-twitter"></i>
              </motion.a>
              <motion.a
                href="https://instagram.com/AutoboyExpress"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                data-tooltip="Follow us on Instagram"
                whileHover={{ scale: 1.2, rotate: -10 }}
                whileTap={{ scale: 0.9 }}
              >
                <i className="fab fa-instagram"></i>
              </motion.a>
              <motion.a
                href="https://facebook.com/AutoboyExpress"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                data-tooltip="Like us on Facebook"
                whileHover={{ scale: 1.2, rotate: 10 }}
                whileTap={{ scale: 0.9 }}
              >
                <i className="fab fa-facebook-f"></i>
              </motion.a>
            </div>
          </div>
        </div>
      </div>

      {/* Support Chat Modal */}
      <SupportChat
        isOpen={supportChatOpen}
        onClose={() => setSupportChatOpen(false)}
      />
    </footer>
  );
};

export default Footer;
